import * as THREE from 'three';
import { SupportResult, TrackSupportPrimitive } from '../core/supports/types';

const STRATEGY_COLORS: Record<string, number> = {
  vertical: 0x9aa5b1,
  leaning: 0x7fb2d9,
  aframe: 0xd9b97f,
  knee: 0xc98fd9,
  forced: 0xd97f7f,
};

/** Steel structure renderer: merged cylinders per primitive, colour-codable by placement strategy. */
export class SupportMesh {
  readonly group = new THREE.Group();
  debugColors = false;
  private meshes: THREE.Mesh[] = [];
  private steelMat = new THREE.MeshStandardMaterial({ color: 0xb9c0c9, roughness: 0.45, metalness: 0.6 });
  private footerMat = new THREE.MeshStandardMaterial({ color: 0x9d9d94, roughness: 0.9, metalness: 0.05 });

  constructor() {
    this.group.name = 'supports';
  }

  rebuild(result: SupportResult): void {
    for (const m of this.meshes) {
      this.group.remove(m);
      m.geometry.dispose();
      if (Array.isArray(m.material)) m.material.forEach((mm) => mm.dispose());
      else if (m.material !== this.steelMat && m.material !== this.footerMat) (m.material as THREE.Material).dispose();
    }
    this.meshes = [];

    const byMaterial = new Map<number | 'steel' | 'footer', THREE.BufferGeometry[]>();
    const push = (key: number | 'steel' | 'footer', geo: THREE.BufferGeometry) => {
      const arr = byMaterial.get(key) ?? [];
      arr.push(geo);
      byMaterial.set(key, arr);
    };

    for (const prim of result.primitives) {
      if (prim.kind === 'footer') {
        const geo = new THREE.CylinderGeometry(prim.radius, prim.radius * 1.15, prim.height, 10);
        geo.translate(prim.pos.x, prim.pos.y + prim.height / 2, prim.pos.z);
        push('footer', geo);
      } else if (prim.kind === 'joint') {
        const geo = new THREE.SphereGeometry(prim.radius, 12, 10);
        geo.translate(prim.pos.x, prim.pos.y, prim.pos.z);
        push('steel', geo);
      } else {
        const geo = tubeBetween(prim);
        if (this.debugColors && prim.kind === 'column') {
          push(STRATEGY_COLORS[prim.strategy] ?? 0xffffff, geo);
        } else {
          push('steel', geo);
        }
      }
    }

    for (const [key, geos] of byMaterial) {
      const merged = mergeGeometries(geos);
      const mat =
        key === 'steel'
          ? this.steelMat
          : key === 'footer'
            ? this.footerMat
            : new THREE.MeshStandardMaterial({ color: key as number, roughness: 0.45, metalness: 0.6 });
      const mesh = new THREE.Mesh(merged, mat);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.group.add(mesh);
      this.meshes.push(mesh);
      for (const g of geos) g.dispose();
    }
  }
}

function tubeBetween(
  prim: Extract<TrackSupportPrimitive, { kind: 'column' | 'brace' | 'knee' | 'stub' }>,
): THREE.BufferGeometry {
  const dir = new THREE.Vector3().subVectors(prim.b, prim.a);
  const len = Math.max(dir.length(), 1e-6);
  const radiusA = prim.kind === 'stub' ? prim.radiusA : prim.radius;
  const radiusB = prim.kind === 'stub' ? prim.radiusB : prim.radius;
  // CylinderGeometry(radiusTop, radiusBottom): after +Y translate, bottom sits at `a`, top at `b`.
  const geo = new THREE.CylinderGeometry(radiusB, radiusA, len, prim.kind === 'column' ? 10 : 8);
  geo.translate(0, len / 2, 0);
  const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
  geo.applyQuaternion(quat);
  geo.translate(prim.a.x, prim.a.y, prim.a.z);
  return geo;
}

/** Minimal local merge (positions+normals+index) — avoids the examples util import. */
function mergeGeometries(geos: THREE.BufferGeometry[]): THREE.BufferGeometry {
  let vCount = 0;
  let iCount = 0;
  for (const g of geos) {
    vCount += g.attributes.position.count;
    iCount += g.index ? g.index.count : g.attributes.position.count;
  }
  const positions = new Float32Array(vCount * 3);
  const normals = new Float32Array(vCount * 3);
  const indices = new Uint32Array(iCount);
  let vOff = 0;
  let iOff = 0;
  for (const g of geos) {
    positions.set(g.attributes.position.array as Float32Array, vOff * 3);
    normals.set(g.attributes.normal.array as Float32Array, vOff * 3);
    const idx = g.index;
    const n = g.attributes.position.count;
    if (idx) {
      for (let i = 0; i < idx.count; i++) indices[iOff + i] = idx.getX(i) + vOff;
      iOff += idx.count;
    } else {
      for (let i = 0; i < n; i++) indices[iOff + i] = i + vOff;
      iOff += n;
    }
    vOff += n;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  out.setIndex(new THREE.BufferAttribute(indices, 1));
  return out;
}
